import store from '@state/store'
import i18n from '@plugins/vue-i18n'
import { PermissionLevel } from '@/shared/constants/permissions/PermissionLevel'
import { PermissionScope } from '@/shared/constants/permissions/PermissionScope'
import { PermissionModifier } from '@/shared/constants/permissions/PermissionModifier'
import { PermissionRequirement } from '@/shared/constants/permissions/PermissionRequirement'
import ErrorPageCodes from '@/shared/constants/error/ErrorPageCodes'

export default [
  {
    path: '/',
    name: 'home',
    component: () => lazyLoadView(import('@views/home.vue')),
    meta: {
      title: i18n.t('home.homePageTitle'),
    },
  },
  {
    path: '/overview',
    name: 'client-overview',
    component: () => lazyLoadView(import('@views/client-overview.vue')),
    meta: {
      title: i18n.t('overview.overviewPageTitle'),
    },
  },
  {
    path: '/timesheets',
    name: 'timesheets',
    component: () => lazyLoadView(import('@views/timesheets.vue')),
    meta: {
      title: i18n.t('timesheets.pageTitle'),
      permissions: {
        requirement: PermissionRequirement.ALL,
        list: [
          {
            level: PermissionLevel.SELECTED_CLIENT_AND_BELOW,
            scope: PermissionScope.TIMESHEETS,
            modifier: PermissionModifier.ALL,
          },
        ],
      },
    },
  },
  {
    path: '/candidates',
    name: 'candidates',
    component: () => lazyLoadView(import('@views/candidates.vue')),
    meta: {
      title: i18n.t('candidates.pageTitle'),
      permissions: {
        requirement: PermissionRequirement.ONE,
        list: [
          {
            level: PermissionLevel.GROUP_AND_BELOW,
            scope: PermissionScope.BOOKING,
            modifier: PermissionModifier.ALL,
          },
          {
            level: PermissionLevel.SELECTED_CLIENT_AND_BELOW,
            scope: PermissionScope.TIMESHEETS,
            modifier: PermissionModifier.ALL,
          },
          {
            level: PermissionLevel.GROUP_AND_BELOW,
            scope: PermissionScope.ACCOUNTS,
            modifier: PermissionModifier.ALL,
          },
        ],
      },
    },
  },
  {
    path: '/help',
    name: 'help',
    component: () => lazyLoadView(import('@views/help.vue')),
    meta: {
      title: i18n.t('help.pageTitle'),
    },
  },
  {
    path: '/bookings',
    component: {
      render(c) {
        return c('router-view')
      },
    },
    children: [
      {
        path: 'create',
        name: 'bookings-create',
        component: () => import('@views/booking-create.vue'),
        meta: {
          title: i18n.t('bookingCreate.pageTitle'),
          permissions: {
            requirement: PermissionRequirement.ALL,
            list: [
              {
                level: PermissionLevel.GROUP_AND_BELOW,
                scope: PermissionScope.BOOKING,
                modifier: PermissionModifier.ALL,
              },
            ],
          },
        },
      },
    ],
  },
  {
    path: '/settings',
    name: 'settings',
    component: () =>
      lazyLoadView(import('@/router/views/settings/settings.vue')),
  },
  {
    path: '/settings/change-password',
    name: 'changePassword',
    component: () =>
      lazyLoadView(
        import('@/router/views/settings/settings-change-password.vue')
      ),
  },
  {
    path: '/login',
    name: 'login',
    component: () => lazyLoadView(import('@views/login.vue')),
    meta: {
      public: true,
      title: i18n.t('login.loginPageTitle'),
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/isUserLoggedIn']) {
          // Redirect to the home page instead
          next({ name: 'home' })
        } else {
          // Continue to the login page
          next()
        }
      },
    },
  },
  {
    path: '/user/impersonate/logout',
    name: 'impersonateLogout',
    component: () => lazyLoadView(import('@views/impersonate-logout.vue')),
    meta: {
      public: true,
      title: 'Impersonate Logout',
    },
  },
  {
    path: '/user/impersonate/:contactId',
    name: 'impersonateLogin',
    component: () => lazyLoadView(import('@views/login.vue')),
    meta: {
      public: true,
      title: 'Impersonate Login',
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/isUserLoggedIn']) {
          // Redirect to the home page instead
          next({ name: 'home' })
        } else {
          // Continue to the login page
          next()
        }
      },
    },
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => lazyLoadView(import('@views/profile.vue')),
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/finance',
    name: 'finance',
    component: () => lazyLoadView(import('@views/finance.vue')),
    meta: {
      permissions: {
        requirement: PermissionRequirement.ALL,
        list: [
          {
            level: null,
            scope: PermissionScope.ACCOUNTS,
            modifier: PermissionModifier.ALL,
          },
        ],
      },
    },
  },
  {
    path: '/profile/:username',
    name: 'username-profile',
    component: () => lazyLoadView(import('@views/profile.vue')),
    meta: {
      // HACK: In order to share data between the `beforeResolve` hook
      // and the `props` function, we must create an object for temporary
      // data only used during route resolution.
      tmp: {},
      beforeResolve(routeTo, routeFrom, next) {
        store
          // Try to fetch the user's information by their username
          .dispatch('users/fetchUser', { username: routeTo.params.username })
          .then((user) => {
            // Add the user to `meta.tmp`, so that it can
            // be provided as a prop.
            routeTo.meta.tmp.user = user
            // Continue to the route.
            next()
          })
          .catch(() => {
            // If a user with the provided username could not be
            // found, redirect to the 404 page.
            next({
              name: '404',
              params: [{ resource: 'User' }, routeTo.path],
              replace: true,
            })
          })
      },
    },
    // Set the user from the route params, once it's set in the
    // beforeResolve route guard.
    props: (route) => ({ user: route.meta.tmp.user }),
  },
  {
    path: '/finance/invoices/:invoiceNo',
    name: 'invoice-view',
    component: () =>
      lazyLoadView(import('@/router/views/finance/invoice-view.vue')),
    meta: {
      title: i18n.t('finance.invoiceViewPageTitle'),
      permissions: {
        requirement: PermissionRequirement.ALL,
        list: [
          {
            level: PermissionLevel.GROUP_AND_BELOW,
            scope: PermissionScope.ACCOUNTS,
            modifier: PermissionModifier.ALL,
          },
        ],
      },
    },
  },
  {
    path: '/logout',
    name: 'logout',
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        store.dispatch('auth/logOut')
        const authRequiredOnPreviousRoute = routeFrom.matched.some(
          (route) => route.meta.authRequired
        )
        // Navigate back to previous page, or home as a fallback
        next(authRequiredOnPreviousRoute ? { name: 'home' } : { ...routeFrom })
      },
    },
  },
  {
    path: '/404',
    name: '404',
    redirect: { name: ErrorPageCodes.PAGE_NOT_FOUND.routeName },
    meta: {
      public: true,
      label: 'Error',
      type: ErrorPageCodes.PAGE_NOT_FOUND.id,
    },
    // Allows props to be passed to the 404 page through route
    // params, such as `resource` to define what wasn't found.
    props: true,
  },
  // Redirect any unmatched routes to the 404 page. This may
  // require some server configuration to work in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  {
    path: '*',
    component: require('@views/_error.vue').default,
    props: true,
    name: ErrorPageCodes.PAGE_NOT_FOUND.routeName,
    meta: {
      public: true,
      label: 'NotFound',
      type: ErrorPageCodes.PAGE_NOT_FOUND.id,
    },
  },
  {
    path: '/500',
    redirect: { name: ErrorPageCodes.INTERNAL_SERVER_ERROR.routeName },
    meta: {
      public: true,
      label: 'Error',
      type: ErrorPageCodes.INTERNAL_SERVER_ERROR.id,
    },
  },
  {
    path: '/401',
    redirect: { name: ErrorPageCodes.UNAUTHORIZED.routeName },
    meta: {
      public: true,
      label: 'Unauthorized',
      type: ErrorPageCodes.UNAUTHORIZED.id,
      icon: 'mdi-account-cancel',
    },
  },
  {
    path: '/403',
    redirect: { name: ErrorPageCodes.FORBIDDEN.routeName },
    meta: {
      public: true,
      label: 'Forbidden',
      type: ErrorPageCodes.FORBIDDEN.id,
      icon: 'mdi-account-cancel',
    },
  },
  {
    path: '/error',
    redirect: { name: ErrorPageCodes.INTERNAL_SERVER_ERROR.routeName },
    meta: {
      public: true,
      label: 'Error',
      type: ErrorPageCodes.INTERNAL_SERVER_ERROR.id,
    },
  },
  {
    path: '*',
    component: require('@views/_error.vue').default,
    props: true,
    name: ErrorPageCodes.INTERNAL_SERVER_ERROR.routeName,
    meta: {
      public: true,
      label: 'Error',
      type: ErrorPageCodes.INTERNAL_SERVER_ERROR.id,
    },
  },
  {
    path: '*',
    component: require('@views/_error.vue').default,
    props: true,
    name: ErrorPageCodes.UNAUTHORIZED.routeName,
    meta: {
      public: true,
      label: 'Unauthorized',
      type: ErrorPageCodes.UNAUTHORIZED.id,
      icon: 'mdi-account-cancel',
    },
  },
  {
    path: '*',
    component: require('@views/_error.vue').default,
    props: true,
    name: ErrorPageCodes.FORBIDDEN.routeName,
    meta: {
      public: true,
      label: 'Forbidden',
      type: ErrorPageCodes.FORBIDDEN.id,
      icon: 'mdi-account-cancel',
    },
  },
  {
    path: '*',
    component: require('@views/_error.vue').default,
    props: true,
    name: ErrorPageCodes.ACCOUNT_LOAD_FAILURE.routeName,
    meta: {
      public: true,
      label: 'Failed To Load Account',
      type: ErrorPageCodes.ACCOUNT_LOAD_FAILURE.id,
      icon: 'mdi-account-cancel',
    },
  },
  {
    path: '*',
    component: require('@views/_error.vue').default,
    props: true,
    name: ErrorPageCodes.NO_SERVER_RESPONSE.routeName,
    meta: {
      public: true,
      label: 'Unable to contact server',
      type: ErrorPageCodes.NO_SERVER_RESPONSE.id,
      icon: 'mdi-account-cancel',
    },
  },
  {
    path: '*',
    component: require('@views/_error.vue').default,
    props: true,
    name: ErrorPageCodes.ACTION_LOCKED.routeName,
    meta: {
      public: true,
      label: 'Action Locked',
      type: ErrorPageCodes.ACTION_LOCKED.id,
      icon: 'mdi-shield-alert',
    },
  },
]

/** Lazy-loads view components, but with better UX. A loading view
 * will be used if the component takes a while to load, falling
 * back to a timeout view in case the page fails to load. You can
 * use this component to lazy-load a route with:
 *
 * component: () => lazyLoadView(import('@views/my-view'))
 *
 * NOTE: Components loaded with this strategy DO NOT have access
 * to in-component guards, such as beforeRouteEnter,
 * beforeRouteUpdate, and beforeRouteLeave. You must either use
 * route-level guards instead or lazy-load the component directly:
 *
 * component: () => import('@views/my-view')
 */
function lazyLoadView(AsyncView) {
  const AsyncHandler = () => ({
    component: AsyncView,
    // A component to use while the component is loading.
    loading: require('@views/_loading.vue').default,
    // Delay before showing the loading component.
    // Default: 200 (milliseconds).
    delay: 400,
    // A fallback component in case the timeout is exceeded
    // when loading the component.
    error: require('@views/_timeout.vue').default,
    // Time before giving up trying to load the component.
    // Default: Infinity (milliseconds).
    timeout: 10000,
  })

  return Promise.resolve({
    functional: true,
    render(h, { data, children }) {
      // Transparently pass any props or children
      // to the view component.
      return h(AsyncHandler, data, children)
    },
  })
}
